import { useCallback } from 'react';
import type { AttributeTreeNode } from '../state/useGenerationState';
import type { ReadyRecommendations } from './useRecommendations';

/** 「指定なし」が選ばれる確率 */
const NONE_PROBABILITY = 0.1;

/** ガチャ対象外のキー */
const DEFAULT_EXCLUDE_KEYS = [
  'main_copy',
  'cta',
  'other_instructions',
  'disclaimer_text',
  'generate_text_from_ec',
  'exclude_text_response',
  'aspect_ratio',
  'age',
  'appearance',
  'gender',
];

export type GachaOptions = {
  attributeTree: AttributeTreeNode[];
  currentAttributes: Record<string, unknown>;
  isSafeMode: boolean;
  excludeKeys?: string[];
  /** 将来のAI推奨属性用: キーごとの候補リスト (未使用・互換のため残置) */
  recommendedOptions?: Record<string, string[]>;
};

export type GachaResult = Record<string, unknown>;

/** GET /generation/recommendations (ready) を選択ロジックで引きやすい形に変換したもの */
type RecoMaps = {
  /** グループ key → 確定選択する option key */
  strongByGroup: Map<string, string>;
  /** グループ key → suggested 候補 (value, score) の配列 */
  suggestedByGroup: Map<string, { value: string; score: number }[]>;
  person: { gender?: string; age?: number };
};

function buildRecoMaps(recommendations: ReadyRecommendations): RecoMaps {
  const strongByGroup = new Map<string, string>();
  for (const entry of recommendations.strong) {
    if (!strongByGroup.has(entry.key)) {
      strongByGroup.set(entry.key, entry.value);
    }
  }

  const suggestedByGroup = new Map<string, { value: string; score: number }[]>();
  for (const entry of recommendations.suggested) {
    const list = suggestedByGroup.get(entry.key) ?? [];
    list.push({ value: entry.value, score: entry.score ?? 0 });
    suggestedByGroup.set(entry.key, list);
  }

  return {
    strongByGroup,
    suggestedByGroup,
    person: recommendations.person || {},
  };
}

/** リーフオプション（選択可能な末端ノード）を再帰的に収集 */
function collectLeafOptions(
  nodes: AttributeTreeNode[] | undefined,
  isSafeMode: boolean,
): AttributeTreeNode[] {
  if (!nodes) return [];

  const leaves: AttributeTreeNode[] = [];
  for (const node of nodes) {
    if (node.key === 'none') continue;
    if (node.metadata?.exclude_from_gacha) continue;

    if (node.children && node.children.length > 0) {
      // グループノード → 子を再帰探索
      leaves.push(...collectLeafOptions(node.children, isSafeMode));
    } else {
      // リーフノード
      if (isSafeMode && node.is_sensitive) continue;
      leaves.push(node);
    }
  }
  return leaves;
}

/** node 自身と子孫 group の key (推奨は「option の直近の親 group の key」で届くため、
 *  体格・身体的特徴 > 体格 > option のような入れ子でも拾えるよう束ねる) */
function collectGroupKeys(node: AttributeTreeNode): string[] {
  const keys = [node.key];
  for (const child of node.children || []) {
    if (child.children && child.children.length > 0) keys.push(...collectGroupKeys(child));
  }
  return keys;
}

/** 重み付きランダム選択 */
function weightedRandomPick(
  options: AttributeTreeNode[],
  isSafeMode: boolean,
): AttributeTreeNode | undefined {
  if (options.length === 0) return undefined;

  // 重み付き選択
  const weights: number[] = options.map(opt =>
    !isSafeMode && opt.is_sensitive ? 1.5 : 1
  );
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  if (totalWeight === 0) return undefined;

  let r = Math.random() * totalWeight;
  for (let i = 0; i < options.length; i++) {
    r -= weights[i];
    if (r <= 0) return options[i];
  }
  return options[options.length - 1];
}

/**
 * suggested 候補の中から最高スコアのものだけを対象にランダムに1つ選ぶ。
 * 「上位候補からランダムに1つ」(計画書§5.3) = 同点1位が複数あれば均等に抽選。
 */
function pickTopSuggested(candidates: { value: string; score: number }[]): string | undefined {
  if (candidates.length === 0) return undefined;
  const maxScore = Math.max(...candidates.map((c) => c.score));
  const top = candidates.filter((c) => c.score === maxScore);
  return top[Math.floor(Math.random() * top.length)].value;
}

/** 属性ノードに対してランダム値を決定 */
function randomizeNode(
  node: AttributeTreeNode,
  currentValue: unknown,
  isSafeMode: boolean,
  excludeKeys: string[],
  reco?: RecoMaps,
): unknown | undefined {
  if (excludeKeys.includes(node.key)) return undefined;
  if (node.metadata?.exclude_from_gacha) return undefined;

  // personグループの特殊処理
  if (node.key === 'person') {
    return randomizePersonGroup(node, currentValue, isSafeMode, excludeKeys, reco);
  }

  // 数値型
  if (node.type === 'numeric') {
    if (excludeKeys.includes(node.key)) return undefined;

    // 30%の確率で「指定なし」
    if (Math.random() < NONE_PROBABILITY) return null;

    const meta = node.metadata || {};
    const min = typeof meta.min === 'number' ? meta.min : 10;
    const max = typeof meta.max === 'number' ? meta.max : 80;
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  // 選択型（children あり）
  if (node.children && node.children.length > 0) {
    const selectionType = typeof node.metadata?.selection === 'string'
      ? (node.metadata.selection as string)
      : 'single';

    if (selectionType === 'group') {
      // グループ型 → 子要素を再帰的に処理
      const currentGroup = (currentValue as Record<string, unknown>) || {};
      const result: Record<string, unknown> = { ...currentGroup };
      let changed = false;

      for (const child of node.children) {
        const childResult = randomizeNode(child, currentGroup[child.key], isSafeMode, excludeKeys, reco);
        if (childResult !== undefined) {
          result[child.key] = childResult;
          changed = true;
        }
      }
      return changed ? result : undefined;
    }

    // AIおまかせ: このグループ (= option の直近の親ノード。backend の group_key と一致) か
    // その子孫 group (入れ子) に strong/suggested があれば、ランダム抽選より優先する。
    const groupKeys = collectGroupKeys(node);
    if (reco && groupKeys.some((k) => reco.strongByGroup.has(k) || reco.suggestedByGroup.has(k))) {
      // 推奨値もこのグループで実際に選択可能な option に限定する。
      // バックエンドは可視性・セーフモードを適用済みだが、exclude_from_gacha は
      // ガチャ固有の除外条件のためクライアント側で最終確認する。
      const selectableKeys = new Set(collectLeafOptions(node.children, isSafeMode).map((o) => o.key));

      const strongValue = groupKeys
        .map((k) => reco.strongByGroup.get(k))
        .find((v) => v !== undefined && selectableKeys.has(v));
      if (strongValue !== undefined) return strongValue;

      const suggestedCandidates = groupKeys
        .flatMap((k) => reco.suggestedByGroup.get(k) ?? [])
        .filter((c) => selectableKeys.has(c.value));
      if (suggestedCandidates.length > 0) {
        const picked = pickTopSuggested(suggestedCandidates);
        if (picked !== undefined) return picked;
      }
    }

    // 推奨が無いグループ: 現行どおりの完全ランダム
    // 単一選択型: 10%の確率で「指定なし」
    if (Math.random() < NONE_PROBABILITY) return 'none';

    const leaves = collectLeafOptions(node.children, isSafeMode);
    const picked = weightedRandomPick(leaves, isSafeMode);
    return picked ? picked.key : undefined;
  }

  return undefined;
}

/** personグループのランダム選択 */
function randomizePersonGroup(
  node: AttributeTreeNode,
  currentValue: unknown,
  isSafeMode: boolean,
  excludeKeys: string[],
  reco?: RecoMaps,
): Record<string, unknown> | undefined {
  const currentGroup = (currentValue as Record<string, unknown>) || {};
  const result: Record<string, unknown> = { ...currentGroup };
  let changed = false;

  // personの子属性を処理（age, gender, appearance は除外。AIおまかせ時は reco.person で別途設定）
  const personExcludeKeys = [...excludeKeys, 'age', 'gender', 'appearance', 'none'];

  for (const child of node.children || []) {
    if (personExcludeKeys.includes(child.key)) continue;

    // ethnicity は常に 'jp' 固定
    if (child.key === 'ethnicity') {
      if (result.ethnicity !== 'jp') {
        result.ethnicity = 'jp';
        changed = true;
      }
      continue;
    }

    const childResult = randomizeNode(child, currentGroup[child.key], isSafeMode, excludeKeys, reco);
    if (childResult !== undefined) {
      result[child.key] = childResult;
      changed = true;
    }
  }

  // AIおまかせ: target.gender/age_range から推定した person.gender/age を反映
  if (reco?.person) {
    if (typeof reco.person.gender === 'string' && reco.person.gender) {
      if (result.gender !== reco.person.gender) {
        result.gender = reco.person.gender;
        changed = true;
      }
    }
    if (typeof reco.person.age === 'number' && Number.isFinite(reco.person.age)) {
      const ageNode = (node.children || []).find((c) => c.key === 'age');
      const meta = ageNode?.metadata || {};
      const min = typeof meta.min === 'number' ? meta.min : 10;
      const max = typeof meta.max === 'number' ? meta.max : 80;
      // 推奨年齢の近傍(±5)でランダム化し、AIおまかせでも引き直す楽しさを残す
      const jitter = Math.floor(Math.random() * 11) - 5;
      const target = Math.max(min, Math.min(max, Math.round(reco.person.age) + jitter));
      if (result.age !== target) {
        result.age = target;
        changed = true;
      }
    }
  }

  // person.none を false にする（人物ありにする）
  if (result.none !== false) {
    result.none = false;
    changed = true;
  }

  return changed ? result : undefined;
}

function runGacha(options: GachaOptions, reco?: RecoMaps): GachaResult {
  const {
    attributeTree,
    currentAttributes,
    isSafeMode,
    excludeKeys = DEFAULT_EXCLUDE_KEYS,
  } = options;

  const result: GachaResult = {};

  for (const node of attributeTree) {
    if (excludeKeys.includes(node.key)) continue;
    // テンプレート group はガチャ対象外 (選ぶとプロンプト全文が置き換わるため)
    if (node.section === 'template') continue;

    const randomized = randomizeNode(
      node,
      currentAttributes[node.key],
      isSafeMode,
      excludeKeys,
      reco,
    );

    if (randomized !== undefined) {
      result[node.key] = randomized;
    }
  }

  return result;
}

/**
 * ガチャ / AIおまかせ（属性選択）フック
 *
 * - `randomize`: 完全ランダム抽選（プロファイル未取得時のフォールバック。旧ガチャと同じ挙動）
 * - `randomizeWithRecommendations`: 商品AIプロファイルの推奨 (strong/suggested) を
 *   優先し、推奨が無いグループのみ完全ランダムにフォールバックする「AIおまかせ」本体
 *   (計画書§5.3)。抽選基盤 (collectLeafOptions/weightedRandomPick/exclude_from_gacha・
 *   セーフモード除外) はそのまま流用。
 */
export function useGacha() {
  const randomize = useCallback((options: GachaOptions): GachaResult => {
    return runGacha(options);
  }, []);

  const randomizeWithRecommendations = useCallback(
    (options: GachaOptions & { recommendations: ReadyRecommendations }): GachaResult => {
      const { recommendations, ...rest } = options;
      return runGacha(rest, buildRecoMaps(recommendations));
    },
    [],
  );

  return { randomize, randomizeWithRecommendations };
}
